import { unwrap, type components } from "@sagot/api-client";
import type { WorkflowTrigger } from "@sagot/constants";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseInfiniteQueryResult, type InfiniteData, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import type { Definition, RunDetail, RunPage, WebhookEndpoint, WorkflowDetail, WorkflowOut, WorkflowStats } from "./types";

type LeadStage = components["schemas"]["StageOut"];
/** The draft fields a save sends. */
interface DraftPatch {
  name?: string;
  description?: string | null;
  definition?: Definition;
  /** The `updated_at` the editor last saw: the server refuses the save when someone else has saved since. */
  base_updated_at?: string;
}

/** The generated body types a definition as a free-form object; spreading the interface gives it the index signature that asks for, so no cast. */
const looseDefinition = (definition: Definition | undefined): Record<string, unknown> | undefined => (definition ? { ...definition } : undefined);

const REFERENCE_STALE_MS = 60_000;
const RUNS_PAGE = 25;

export const workflowKeys = {
  all: (ws: string) => ["ws", ws, "workflows"] as const,
  /** The Workflows page's list: the default workflows and the workspace's own. `all` holds the workspace's own alone. */
  list: (ws: string) => ["ws", ws, "workflows", "list"] as const,
  detail: (ws: string, id: string) => ["ws", ws, "workflows", id] as const,
  runs: (ws: string, id: string) => ["ws", ws, "workflows", id, "runs"] as const,
  run: (ws: string, id: string, runId: string) => ["ws", ws, "workflows", id, "runs", runId] as const,
  stats: (ws: string, id: string, days: number) => ["ws", ws, "workflows", id, "stats", days] as const,
  /** Under the workflow's own key, so every save / publish / pause (which invalidates `all`) refreshes it. */
  setup: (ws: string, id: string) => ["ws", ws, "workflows", id, "setup"] as const,
  endpoints: (ws: string) => ["ws", ws, "workflows", "endpoints"] as const,
  stages: (ws: string) => ["ws", ws, "workflows", "stages"] as const,
};

export function useWorkflowList(enabled = true): UseQueryResult<WorkflowOut[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({ queryKey: workflowKeys.all(ws), queryFn: () => unwrap(api.GET("/v1/workflows", { params: { query: { origin: "user" } } })), retry: false, enabled });
}

/** The Workflows page's list: the default workflows first, then the workspace's own (every plan may read it). */
export function useWorkflowsWithDefaults(enabled = true): UseQueryResult<WorkflowOut[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({ queryKey: workflowKeys.list(ws), queryFn: () => unwrap(api.GET("/v1/workflows")), retry: false, enabled });
}

export function useWorkflow(id: string): UseQueryResult<WorkflowDetail> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: workflowKeys.detail(ws, id),
    queryFn: () => unwrap(api.GET("/v1/workflows/{workflow_id}", { params: { path: { workflow_id: id } } })),
    retry: false,
  });
}

/** Runs newest first, a page at a time: `fetchNextPage` loads the ones started before the last row. */
export function useWorkflowRuns(id: string, enabled: boolean): UseInfiniteQueryResult<InfiniteData<RunPage, string | null>> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: workflowKeys.runs(ws, id),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      unwrap(api.GET("/v1/workflows/{workflow_id}/runs", { params: { path: { workflow_id: id }, query: { limit: RUNS_PAGE, ...(pageParam ? { before: pageParam } : {}) } } })),
    getNextPageParam: (last) => last.next_before,
    enabled,
  });
}

export function useWorkflowRun(id: string, runId: string | null): UseQueryResult<RunDetail> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: workflowKeys.run(ws, id, runId ?? ""),
    queryFn: () => unwrap(api.GET("/v1/workflows/{workflow_id}/runs/{run_id}", { params: { path: { workflow_id: id, run_id: runId ?? "" } } })),
    enabled: runId !== null,
  });
}

export function useWorkflowStats(id: string, days: number, enabled: boolean): UseQueryResult<WorkflowStats> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: workflowKeys.stats(ws, id, days),
    queryFn: () => unwrap(api.GET("/v1/workflows/{workflow_id}/stats", { params: { path: { workflow_id: id }, query: { days } } })),
    enabled,
  });
}

/** Webhook endpoints a Send webhook step can target (optional: needs webhooks.manage). */
export function useEndpoints(enabled: boolean): UseQueryResult<WebhookEndpoint[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({ queryKey: workflowKeys.endpoints(ws), queryFn: () => unwrap(api.GET("/v1/webhooks")), enabled, retry: false, staleTime: REFERENCE_STALE_MS });
}

export function useLeadStages(enabled: boolean): UseQueryResult<LeadStage[]> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({ queryKey: workflowKeys.stages(ws), queryFn: () => unwrap(api.GET("/v1/lead-stages")), enabled, retry: false, staleTime: REFERENCE_STALE_MS });
}

export function useWorkflowMutations(id?: string) {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const settle = (detail?: WorkflowDetail) => {
    if (detail) client.setQueryData(workflowKeys.detail(ws, detail.id), detail);
    return client.invalidateQueries({ queryKey: workflowKeys.all(ws) });
  };
  const path = { params: { path: { workflow_id: id ?? "" } } };
  /** A new draft; `definition` starts it from a template (the server checks it like any save). */
  const create = useMutation({
    mutationFn: (body: { name: string; trigger: WorkflowTrigger; description?: string; definition?: Definition }) => unwrap(api.POST("/v1/workflows", { body: { ...body, definition: looseDefinition(body.definition) } })),
    onSuccess: settle,
  });
  const save = useMutation({
    mutationFn: (body: DraftPatch) => unwrap(api.PATCH("/v1/workflows/{workflow_id}", { ...path, body: { ...body, definition: looseDefinition(body.definition) } })),
    onSuccess: settle,
  });
  const publish = useMutation({ mutationFn: () => unwrap(api.POST("/v1/workflows/{workflow_id}/publish", path)), onSuccess: settle });
  const pause = useMutation({ mutationFn: () => unwrap(api.POST("/v1/workflows/{workflow_id}/pause", path)), onSuccess: settle });
  const resume = useMutation({ mutationFn: () => unwrap(api.POST("/v1/workflows/{workflow_id}/resume", path)), onSuccess: settle });
  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/workflows/{workflow_id}", path)),
    // Only the lists are refreshed: refetching the deleted workflow answers 404 and would swap the builder for an error page before it navigates away.
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: workflowKeys.all(ws), exact: true }), client.invalidateQueries({ queryKey: workflowKeys.list(ws), exact: true })]),
  });
  const reorder = useMutation({
    mutationFn: (body: { trigger: WorkflowTrigger; workflow_ids: string[] }) => unwrap(api.POST("/v1/workflows/reorder", { body })),
    onSuccess: () => settle(),
  });
  return { create, save, publish, pause, resume, remove, reorder };
}
